import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import MarketingShell from "@/components/MarketingShell";
import SentenceLines from "@/components/SentenceLines";
import JmlLink from "@/components/JmlLink";
import { formatApiErrorDetail } from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.4, 0, 0.2, 1];

// Five moments, one line each. Restraint is the whole point here.
const MOMENTS = [
  { title: "A birth.", line: "A lullaby with their name inside it." },
  { title: "Thirteen.", line: "The gift nobody else will think of." },
  { title: "A wedding.", line: "Two names. One piece of music." },
  { title: "Illness.", line: "Something written for them, to make the hours softer." },
  { title: "A memory.", line: "The piece that plays while everyone remembers." },
];

const reveal = {
  initial: { opacity: 0, y: 12 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.25 },
  transition: { duration: 0.8, ease: EASE },
};

export default function Commissions() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (!name.trim() || !email.trim()) {
      toast.error("A name and an address and I'll write back.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/inquiries`, {
        name: name.trim(),
        email: email.trim(),
        message: message.trim(),
        inquiry_type: "commission",
      });
      setSent(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err));
    } finally {
      setBusy(false);
    }
  };

  const field = {
    backgroundColor: "#241246",
    border: "1px solid #1A0F3C",
    color: "#F5EFE3",
  };

  return (
    <MarketingShell active="/commissions">
      <motion.section
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: EASE }}
        data-testid="commissions-hero"
      >
        <p className="text-[11px] uppercase mb-4" style={{ color: "#F2D589", letterSpacing: "0.28em" }}>
          Commissions
        </p>
        <h1
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontWeight: 300,
            fontSize: "var(--step-3)",
            letterSpacing: "-0.02em",
            lineHeight: 1.12,
            maxWidth: "26ch",
          }}
          data-testid="commissions-headline"
        >
          <SentenceLines text="A name, a verse, a moment. Written as music." />
        </h1>
        <p className="mt-8 max-w-[68ch] text-base sm:text-lg" style={{ color: "#C6BBA9", lineHeight: 1.7 }}>
          The letters of a name go through the mapping method, and what comes out is played and
          recorded for the person it belongs to. One piece, theirs, and nobody else&apos;s.
        </p>
      </motion.section>

      <motion.section {...reveal} className="mt-24" data-testid="commissions-moments">
        <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, fontSize: "var(--step-2)" }}>
          For when it matters
        </h2>
        <div className="mt-14 grid gap-x-16 gap-y-12 sm:grid-cols-2 lg:grid-cols-3">
          {MOMENTS.map((m, i) => (
            <motion.div
              key={m.title}
              {...reveal}
              transition={{ duration: 0.8, ease: EASE, delay: i * 0.06 }}
              className="pl-7"
              style={{ borderLeft: "1px solid rgba(242,213,137,0.3)" }}
              data-testid={`commission-moment-${i}`}
            >
              <p style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, fontSize: "var(--step-1)" }}>
                {m.title}
              </p>
              <p className="mt-3 text-base" style={{ color: "#C6BBA9", lineHeight: 1.6 }}>
                {m.line}
              </p>
            </motion.div>
          ))}
        </div>
      </motion.section>

      <motion.section {...reveal} className="mt-24 max-w-[36rem]" data-testid="commissions-form-section">
        <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, fontSize: "var(--step-2)" }}>
          <SentenceLines text="Tell me who it's for." />
        </h2>
        <p className="mt-6 text-base" style={{ color: "#C6BBA9", lineHeight: 1.7 }}>
          No form to fill out twice and no price list to work through — write to me about the person
          and the occasion, and I&apos;ll come back to you with what it would take.
        </p>

        {sent ? (
          <div className="mt-10" data-testid="commissions-sent">
            <p
              style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, fontSize: "var(--step-1)", color: "#F2D589" }}
            >
              It&apos;s with me.
            </p>
            <p className="mt-3 text-base" style={{ color: "#C6BBA9", lineHeight: 1.7 }}>
              I read these myself and I&apos;ll write back personally.
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-10 flex flex-col gap-3" data-testid="commissions-form">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
              autoComplete="name"
              data-testid="commission-name"
              className="h-12 w-full rounded-md px-4 text-sm outline-none"
              style={field}
            />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email address"
              autoComplete="email"
              data-testid="commission-email"
              className="h-12 w-full rounded-md px-4 text-sm outline-none"
              style={field}
            />
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Who is it for, and what's the occasion?"
              rows={5}
              data-testid="commission-message"
              className="w-full rounded-md px-4 py-3 text-sm outline-none"
              style={field}
            />
            <button
              type="submit"
              disabled={busy}
              data-testid="commission-submit"
              className="mt-3 inline-flex items-center justify-center whitespace-nowrap rounded-full px-7 h-12 text-xs uppercase transition-colors hover:bg-[#F2D589]/[0.12] disabled:opacity-50"
              style={{
                backgroundColor: "transparent",
                color: "#F2D589",
                border: "1px solid #F2D589",
                letterSpacing: "0.18em",
                fontWeight: 500,
              }}
            >
              {busy ? "Sending" : "Send it to Dan"}
            </button>
          </form>
        )}

        <p className="mt-8 text-sm" style={{ color: "rgba(245,239,227,0.5)", lineHeight: 1.7 }}>
          Curious what the method sounds like first?{" "}
          <JmlLink className="transition-colors hover:text-[#FFD9C2]" style={{ color: "#FCA07A" }} testId="commissions-tryit-link">
            Put a name through it
          </JmlLink>
        </p>
      </motion.section>
    </MarketingShell>
  );
}
